// src/components/settings/DesignationManagement.js
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { settingsService } from '../../services/settingsService';
import { organizationService } from '../../services/organizationService';
import LoadingSpinner from '../common/LoadingSpinner';
import Toast from '../common/Toast';
import Pagination from '../common/Pagination';
import Modal from '../common/Modal';

const DesignationManagement = () => {
    const { user } = useAuth();
    const [designations, setDesignations] = useState([]);
    const [organizationCategories, setOrganizationCategories] = useState([]);
    const [premises, setPremises] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingDesignation, setEditingDesignation] = useState(null);
    const [formData, setFormData] = useState({ designation_name: '', org_category_id: '', premises_ids: [], status: 1 });
    const [formErrors, setFormErrors] = useState({});
    const [saveLoading, setSaveLoading] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 20,
        total: 0,
        total_pages: 1
    });

    const isClientOrAppAdmin = user?.user_type === 'client_admin' || user?.user_type === 'app_admin';

    // Load organization categories for core_admin
    const loadOrganizationCategories = useCallback(async () => {
        if (user?.user_type !== 'core_admin') return;

        try {
            const response = await organizationService.getOrganizationCategories();
            // Response could be array directly or wrapped in an object
            const categories = Array.isArray(response) ? response : (response.organization_categories || response.categories || []);
            setOrganizationCategories(categories);
        } catch (error) {
            console.error('Error loading organization categories:', error);
            setOrganizationCategories([]);
        }
    }, [user]);

    // Load premises for client_admin and app_admin
    const loadPremises = useCallback(async () => {
        if (!isClientOrAppAdmin || !user?.org_id) return;

        try {
            const response = await organizationService.getOrganizationPremises(user.org_id);
            // Response could be array directly or wrapped in an object
            const premisesList = Array.isArray(response) ? response : (response.premises || []);
            setPremises(premisesList);
        } catch (error) {
            console.error('Error loading premises:', error);
            setPremises([]);
        }
    }, [user, isClientOrAppAdmin]);

    useEffect(() => {
        loadOrganizationCategories();
        loadPremises();
    }, [loadOrganizationCategories, loadPremises]);

    const loadDesignations = useCallback(async () => {
        try {
            setLoading(true);
            const params = {
                page: pagination.current_page,
                limit: pagination.per_page,
                search: searchTerm
            };
            const response = await settingsService.getDesignations(params);
            setDesignations(response.designations || []);
            if (response.pagination) {
                setPagination(response.pagination);
            }
        } catch (error) {
            showToast(error.message, 'error');
        } finally {
            setLoading(false);
        }
    }, [pagination.current_page, pagination.per_page, searchTerm]);

    useEffect(() => {
        loadDesignations();
    }, [loadDesignations]);

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
    };

    const handleAddClick = () => {
        setEditingDesignation(null);
        setFormData({
            designation_name: '',
            org_category_id: user?.org_category_id || '',
            premises_ids: [],
            status: 1
        });
        setFormErrors({});
        setShowModal(true);
    };

    const handleEditClick = (designation) => {
        setEditingDesignation(designation);
        setFormData({
            designation_name: designation.designation_name || '',
            org_category_id: designation.org_category_id || '',
            premises_ids: designation.premises_id ? [designation.premises_id] : [],
            status: designation.status ?? 1
        });
        setFormErrors({});
        setShowModal(true);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? (checked ? 1 : 0) : value }));
        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handlePremisesChange = (e) => {
        const options = e.target.options;
        const selected = [];
        for (let i = 0; i < options.length; i++) {
            if (options[i].selected) {
                selected.push(parseInt(options[i].value));
            }
        }
        setFormData(prev => ({ ...prev, premises_ids: selected }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.designation_name.trim()) errors.designation_name = 'Designation name is required';

        if (user?.user_type === 'core_admin') {
            if (!formData.org_category_id) errors.org_category_id = 'Organization category is required';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            setSaveLoading(true);

            // Prepare data based on user role
            const submitData = {
                designation_name: formData.designation_name,
                status: formData.status
            };

            if (user?.user_type === 'core_admin') {
                submitData.org_category_id = formData.org_category_id;
                // Core admin always has premises_id = 0
            } else {
                // client_admin and app_admin
                if (!editingDesignation && formData.premises_ids.length > 0) {
                    submitData.premises_ids = formData.premises_ids;
                }
            }

            if (editingDesignation) {
                await settingsService.updateDesignation(editingDesignation.id, submitData);
                showToast('Designation updated successfully', 'success');
            } else {
                await settingsService.createDesignation(submitData);
                showToast('Designation created successfully', 'success');
            }
            setShowModal(false);
            loadDesignations();
        } catch (error) {
            showToast(error.message, 'error');
        } finally {
            setSaveLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this designation?')) return;

        try {
            await settingsService.deleteDesignation(id);
            showToast('Designation deleted successfully', 'success');
            loadDesignations();
        } catch (error) {
            showToast(error.message, 'error');
        }
    };

    return (
        <div>
            <Toast
                show={toast.show}
                message={toast.message}
                type={toast.type}
                onClose={() => setToast({ show: false, message: '', type: 'success' })}
            />

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#333' }}>
                        Designation Management
                    </h2>
                    <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '13px' }}>
                        Manage job designations for your organization
                    </p>
                </div>
                <button
                    onClick={handleAddClick}
                    style={{
                        padding: '10px 20px',
                        backgroundColor: '#007bff',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                >
                    <i className="fas fa-plus"></i>
                    Add Designation
                </button>
            </div>

            {/* Search */}
            <div style={{ marginBottom: '20px' }}>
                <input
                    type="text"
                    placeholder="Search designations..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                        width: '100%',
                        maxWidth: '400px',
                        padding: '10px 15px',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '14px'
                    }}
                />
            </div>

            {/* Table */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <LoadingSpinner />
                </div>
            ) : designations.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                    <i className="fas fa-briefcase" style={{ fontSize: '48px', marginBottom: '15px', opacity: 0.3 }}></i>
                    <h3>No designations found</h3>
                    <p>Try adjusting your search or add a new designation</p>
                </div>
            ) : (
                <>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            backgroundColor: '#fff',
                            borderRadius: '8px',
                            overflow: 'hidden'
                        }}>
                            <thead style={{ backgroundColor: '#f8f9fa' }}>
                                <tr>
                                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#333', fontSize: '13px' }}>ID</th>
                                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#333', fontSize: '13px' }}>Designation Name</th>
                                    {user?.user_type === 'core_admin' && (
                                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#333', fontSize: '13px' }}>Organization Category</th>
                                    )}
                                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#333', fontSize: '13px' }}>Premises</th>
                                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#333', fontSize: '13px' }}>Status</th>
                                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#333', fontSize: '13px' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {designations.map((designation) => (
                                    <tr key={designation.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                                        <td style={{ padding: '12px', fontSize: '14px' }}>{designation.id}</td>
                                        <td style={{ padding: '12px', fontSize: '14px', fontWeight: '500' }}>{designation.designation_name}</td>
                                        {user?.user_type === 'core_admin' && (
                                            <td style={{ padding: '12px', fontSize: '14px' }}>{designation.category_name || '-'}</td>
                                        )}
                                        <td style={{ padding: '12px', fontSize: '14px' }}>{designation.premises_name || '-'}</td>
                                        <td style={{ padding: '12px', textAlign: 'center' }}>
                                            <span style={{
                                                backgroundColor: designation.status == 1 ? '#28a745' : '#dc3545',
                                                color: '#fff',
                                                padding: '4px 8px',
                                                borderRadius: '4px',
                                                fontSize: '12px',
                                                fontWeight: '600'
                                            }}>
                                                {designation.status == 1 ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td style={{ padding: '12px', textAlign: 'center' }}>
                                            <button
                                                onClick={() => handleEditClick(designation)}
                                                style={{
                                                    padding: '6px 12px',
                                                    backgroundColor: '#007bff',
                                                    color: '#fff',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '13px',
                                                    marginRight: user?.user_type === 'core_admin' ? '8px' : '0'
                                                }}
                                            >
                                                <i className="fas fa-edit"></i> Edit
                                            </button>
                                            {user?.user_type === 'core_admin' && (
                                                <button
                                                    onClick={() => handleDelete(designation.id)}
                                                    style={{
                                                        padding: '6px 12px',
                                                        backgroundColor: '#dc3545',
                                                        color: '#fff',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        fontSize: '13px'
                                                    }}
                                                >
                                                    <i className="fas fa-trash"></i> Delete
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div style={{ marginTop: '20px' }}>
                        <Pagination
                            currentPage={pagination.current_page}
                            totalPages={pagination.total_pages}
                            onPageChange={(page) => setPagination(prev => ({ ...prev, current_page: page }))}
                        />
                    </div>
                </>
            )}

            {/* Add/Edit Modal */}
            <Modal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                title={editingDesignation ? 'Edit Designation' : 'Add Designation'}
                size="medium"
            >
                <form onSubmit={handleSubmit}>
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                            Designation Name <span style={{ color: 'red' }}>*</span>
                        </label>
                        <input
                            type="text"
                            name="designation_name"
                            value={formData.designation_name}
                            onChange={handleInputChange}
                            style={{
                                width: '100%',
                                padding: '10px',
                                border: formErrors.designation_name ? '1px solid #dc3545' : '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '14px'
                            }}
                            placeholder="Enter designation name"
                        />
                        {formErrors.designation_name && <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>{formErrors.designation_name}</div>}
                    </div>

                    {/* Organization Category - Core Admin Only */}
                    {user?.user_type === 'core_admin' && (
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                                Organization Category <span style={{ color: 'red' }}>*</span>
                            </label>
                            <select
                                name="org_category_id"
                                value={formData.org_category_id}
                                onChange={handleInputChange}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    border: formErrors.org_category_id ? '1px solid #dc3545' : '1px solid #ddd',
                                    borderRadius: '4px',
                                    fontSize: '14px'
                                }}
                            >
                                <option value="">Select organization category</option>
                                {organizationCategories.map(category => (
                                    <option key={category.id} value={category.id}>{category.category_name}</option>
                                ))}
                            </select>
                            {formErrors.org_category_id && <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>{formErrors.org_category_id}</div>}
                        </div>
                    )}

                    {/* Premises Selection - Client Admin and App Admin Only */}
                    {isClientOrAppAdmin && !editingDesignation && (
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                                Apply to Premises (hold Ctrl/Cmd to select multiple)
                            </label>
                            <select
                                multiple
                                value={formData.premises_ids}
                                onChange={handlePremisesChange}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    fontSize: '14px',
                                    minHeight: '120px'
                                }}
                            >
                                <option value="0">All Premises in Organization</option>
                                {premises.map(premise => (
                                    <option key={premise.id} value={premise.id}>{premise.premises_name}</option>
                                ))}
                            </select>
                            <div style={{ color: '#666', fontSize: '12px', marginTop: '5px' }}>
                                Leave empty to add to all premises, or select specific premises
                            </div>
                        </div>
                    )}

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                name="status"
                                checked={formData.status === 1}
                                onChange={handleInputChange}
                                style={{ marginRight: '8px' }}
                            />
                            <span style={{ fontWeight: '500', fontSize: '14px' }}>Active</span>
                        </label>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #ddd' }}>
                        <button
                            type="button"
                            onClick={() => setShowModal(false)}
                            style={{
                                padding: '10px 20px',
                                backgroundColor: '#6c757d',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '14px'
                            }}
                            disabled={saveLoading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            style={{
                                padding: '10px 20px',
                                backgroundColor: '#007bff',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: saveLoading ? 'not-allowed' : 'pointer',
                                fontSize: '14px',
                                opacity: saveLoading ? 0.7 : 1,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                            disabled={saveLoading}
                        >
                            {saveLoading && <LoadingSpinner size="small" />}
                            {saveLoading ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default DesignationManagement;
